import React, { useEffect, useRef } from 'react';
import { prefersReducedMotion } from '../../utils/noise';

const PIXEL_SIZE = 4;
const COLORED_PIXELS = 20;
const UPDATE_INTERVAL = 1000;

const PRIMARIES = [
    [255, 0, 0],
    [0, 255, 0],
    [0, 0, 255]
];

/**
 * Faint grain across the page, with a handful of RGB pixels that jump to
 * new positions once a second.
 *
 * The previous version drove this with requestAnimationFrame and an
 * elapsed-time guard, so the callback fired ~60 times a second and
 * early-returned on 59 of them — a permanent wake-up on every page for an
 * effect that changes once a second. A plain interval does the same job
 * without keeping the compositor busy.
 */
const StaticBackground: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const reduceMotion = prefersReducedMotion();
        let grain: ImageData | null = null;
        let timer: ReturnType<typeof setInterval> | undefined;

        const drawColoredPixels = () => {
            if (!grain) return;
            ctx.putImageData(grain, 0, 0);
            for (let i = 0; i < COLORED_PIXELS; i++) {
                const x = Math.floor(Math.random() * (canvas.width / PIXEL_SIZE)) * PIXEL_SIZE;
                const y = Math.floor(Math.random() * (canvas.height / PIXEL_SIZE)) * PIXEL_SIZE;
                const [r, g, b] = PRIMARIES[Math.floor(Math.random() * PRIMARIES.length)];
                ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
                ctx.fillRect(x, y, PIXEL_SIZE, PIXEL_SIZE);
            }
        };

        const buildGrain = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;

            const imageData = ctx.createImageData(canvas.width, canvas.height);
            const data = imageData.data;

            // One random value per PIXEL_SIZE block, written across the
            // block — same output as the old four-deep nested loop, one
            // pass instead of four.
            for (let y = 0; y < canvas.height; y += PIXEL_SIZE) {
                for (let x = 0; x < canvas.width; x += PIXEL_SIZE) {
                    const value = Math.random() * 255;
                    const maxDy = Math.min(PIXEL_SIZE, canvas.height - y);
                    const maxDx = Math.min(PIXEL_SIZE, canvas.width - x);
                    for (let dy = 0; dy < maxDy; dy++) {
                        let i = ((y + dy) * canvas.width + x) * 4;
                        for (let dx = 0; dx < maxDx; dx++) {
                            data[i] = value;
                            data[i + 1] = value;
                            data[i + 2] = value;
                            data[i + 3] = 13; // 255 * 0.05
                            i += 4;
                        }
                    }
                }
            }

            grain = imageData;
            drawColoredPixels();
        };

        const start = () => {
            buildGrain();
            if (timer) clearInterval(timer);
            if (!reduceMotion) {
                timer = setInterval(drawColoredPixels, UPDATE_INTERVAL);
            }
        };

        start();
        window.addEventListener('resize', start);

        return () => {
            window.removeEventListener('resize', start);
            if (timer) clearInterval(timer);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="noise-background"
            aria-hidden="true"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: -1,
                pointerEvents: 'none'
            }}
        />
    );
};

export default StaticBackground;
